import { Config, PeopleProps } from "./type";
import { isArray } from "./utils";

function _apiLog(msg: string, type: 'warn' | 'error' = 'warn') {
  if (DATrackerInner.apiSupportLog) {
    console[type](`APILOG: ${msg}`)
  }
}

function _debugLog(msg: string, type: 'log' = 'log') {
  if (DATrackerInner.devLog) {
    console[type](`%cHUBBLE DEBUG: ${msg}`, 'color: blue')
  }
}

const HUBBLE_TO_COUNTLY_PV = 'hubble_to_countly_pv'

/**
 * 初始化埋点
 * @param token 
 * @param config 
 * @param name 
 */
export const init = (token: string, config: Config, name: string): void => {
  _initConfig(config)

  _initCountly(token, config)

  if (name && name !== 'DATracker') {
    _apiLog('countly暂不支持多实例')
  }
};

/**
 * 初始化配置
 * @param config 
 */
function _initConfig(config: Config) {
  const { apiSupportLog, devLog, persistence } = config

  DATrackerInner.apiSupportLog = apiSupportLog
  DATrackerInner.devLog = devLog

  if (persistence === 'localStorage') {
    _apiLog('countly仅支持localStorage和内存存储，不支持cookie')
  }
}

function login(userId: string | number) {
  _debugLog(`登录，${Countly.get_device_id_type()}，${Countly.DeviceIdType.DEVELOPER_SUPPLIED}，${userId}`)
  if (Countly.get_device_id_type() === Countly.DeviceIdType.DEVELOPER_SUPPLIED) {
    // 如果是切换账号重新登录，此时由于上个账号登录时设置过User ID，get_device_id_type()会返回
    // DEVELOPER_SUPPLIED 从而进入这个条件，对于这种情况调change_id设置新的User ID并且第三个参数传false
    // 代表不要合并上个用户的数据。
    Countly.change_id(userId, false)
  } else {
    // 如果get_device_id_type返回的不是DEVELOPER_SUPPLIED，代表完全是第一次登录，从来设置过User ID，
    // 此时需要设置User ID并合并登录前的数据，登录前Countly会将所有用户数据和一个临时生成的User ID关联起来，
    // 调用change_id并且第三个参数传true会将临时User ID的数据都合并到新登录的这个真正的User ID下面来，
    // 这个方法调用后Countly sdk内部会将device id的type设置为DEVELOPER_SUPPLIED，下次切换账号重新登录
    // 就会走到if条件。
    Countly.change_id(userId, true)
  }
}

/**
 * 初始化Countly
 * @param token 
 * @param config 
 */
function _initCountly(token: string, config: Config) {
  const {
    truncateLength,
    debug,
    pvConfig = {
      open: true,
      key: HUBBLE_TO_COUNTLY_PV,
      segmentation: {},
    },
    loaded,
  } = config
  Countly.q = Countly.q || [];

  const { open: pvOpen = true, key: pvKey = HUBBLE_TO_COUNTLY_PV, segmentation: pvSegmentation = {} } = pvConfig

  if (!Countly.app_key) {
    // umi插件中会提前初始化
    Countly.app_key = token
  }
  Countly.url = "https://countly.unus.netease.com";
  if (truncateLength) {
    Countly.maxValueSize = truncateLength
  }
  if (debug) {
    Countly.debug = debug
  }

  Countly.q.push(["track_sessions"]);
  Countly.q.push(["track_pageview"]);
  Countly.q.push(["track_clicks"]);
  Countly.q.push(["track_scrolls"]);
  Countly.q.push(["track_errors"]);
  Countly.q.push(["track_links"]);
  Countly.q.push(["track_forms"]);
  Countly.q.push(["collect_from_forms"]);

  function onload() {
    Countly.init()
    loaded?.(DATrackerInner)
    init_snippet_todo.forEach((item: Function) => item())
    if (pvOpen) {
      // 自动为业务埋一个pv的点
      Countly?.add_technology_event({
        key: pvKey,
        segmentation: pvSegmentation,
      })
    }
  }
  // countly加载成功之后
  if (window.isCountlyLoaded) {
    window.readyOnLoadfn = undefined;
    onload()
  } else {
    window.readyOnLoadfn = onload;
  }
}

/**
 * 获取登录用户
 * @returns 
 */
function get_user_id() {
  return Countly.device_id
}

/**
 * 埋点方法
 * @param event_name 
 * @param properties 
 * @param callback 
 * @param setData_type 
 * @param method 
 */
function track(event_name: string, properties: any, callback: any, setData_type: any, method: any) {
  if (callback) {
    _apiLog(`track callback暂不支持`)
  }
  if (setData_type) {
    _apiLog(`track setData_type暂不支持`)
  }
  if (method) {
    _apiLog(`track method暂不支持`)
  }

  // 业务事件
  Countly.add_business_event({
    key: event_name,
    segmentation: properties,
  })
}

/**
 * 设置事件通用属性
 * @param properties 
 */
function register_attributes(properties: any) {
  Countly.user_details({
    custom: properties,
  })
}


/**
 * 删除一个通用属性
 * @param eventName 
 */
function unregister_attributes(eventName: string) {
  Countly.user_details({
    custom: {
      [eventName]: ''
    },
  })
}

/**
 * 清除所有通用属性
 */
function clear_attributes() {
  Countly.user_details({
    custom: {},
  })
}

/**
 * 手动触发pv
 * @param options 
 */
function track_pageview(options: any) {
  const url = location.href
  Countly.q.push(['add_event', {
    key: "link_pv",
    segmentation: {
        url: url,
        ...options
    }
  }]);
}

/**
 * People信息
 */
const People: any = function () { }
People.prototype.userDetailData = {}
People.prototype.set = (properties: PeopleProps) => {
  if (!properties || Object.keys(properties).length === 0) {
    return
  }
  const supportAttrs: (keyof PeopleProps)[] = [
    'name',
    'username',
    'email',
    'organization',
    'phone',
    'picture',
    'gender',
    'byear',
  ]
  _debugLog(`people.set目前仅支持${supportAttrs.join(',')}，其他字段会放到custom中`)
  const innerAttrs: Partial<Record<keyof PeopleProps, any>> = {}
  const custom: Record<string, any> = {}
  Object.entries(properties).forEach(([key, value]) => {
    const attr = key as keyof PeopleProps
    if (supportAttrs.includes(attr) && value) {
      innerAttrs[attr] = _handlePeopleAttr(attr, value)
    } else {
      custom[attr] = value
    }
  })
  People.prototype.userDetailData = {
    ...innerAttrs,
    custom,
  }
  Countly.user_details(People.prototype.userDetailData)
}

function _addPeopleAttr(key: string, value: any, isCustom: boolean = false) {
  if (isCustom) {
    if (People.prototype.userDetailData.custom) {
      People.prototype.userDetailData.custom[key] = value
    } else {
      People.prototype.userDetailData.custom = {
        [key]: value
      }
    }
  } else {
    People.prototype.userDetailData[key] = value
  }
  Countly.user_details(People.prototype.userDetailData)
}

People.prototype.set_realname = (realname: string) => {
  _addPeopleAttr('realname', realname, true)
}

People.prototype.set_country = (country: string) => {
  _addPeopleAttr('country', country, true)
}

People.prototype.set_province = (province: string) => {
  _addPeopleAttr('province', province, true)
}

People.prototype.set_city = (city: string) => {
  _addPeopleAttr('city', city, true)
}

People.prototype.set_age = (age: number) => {
  _addPeopleAttr('age', age, true)
}

People.prototype.set_gender = (gender: number) => {
  _addPeopleAttr('gender', _handlePeopleAttr('gender', gender))
}

/**
 * people字段转化
 *  */
function _handlePeopleAttr(key: keyof PeopleProps, value: any) {
  if (key === 'gender') {
    return (
      (
        {
          0: 'W',
          1: 'M',
          2: 'unknow',
        } as any
      )[value] || value
    )
  } else {
    return value
  }
}

// 待执行的初始化方法
const init_snippet_todo: any = []

export function init_from_snippet() {
  const track_snippet_todo: any = []; // 最后执行
  // @ts-ignore
  if (window.DATracker) {
    // @ts-ignore 先初始化
    window.DATracker['_i'].forEach(value => {
      const [token, config, name] = value
      DATrackerInner.init(token, config, name)
    })
    // @ts-ignore
    delete window.DATracker['_i']
    // @ts-ignore
    Object.entries(window.DATracker).forEach(([key, value]: any) => {
      if (isArray(value) && key !== 'people') {
        const [event_name, ...properties] = value
        if (event_name !== 'track') {
          init_snippet_todo.push(DATrackerInner[event_name].bind(null, ...properties));
        } else {
          track_snippet_todo.push(DATrackerInner[event_name].bind(null, ...properties))
        }
      }
    })
    // @ts-ignore
    Object.values(window.DATracker?.people || []).forEach((value: any) => {
      if (isArray(value)) {
        const [event_name, ...properties] = value
        init_snippet_todo.push(DATrackerInner.people[event_name].bind(null, ...properties))
      }
    })

    init_snippet_todo.push(...track_snippet_todo)
  }
}

var DATrackerInner: any = new Proxy(
  {
    init,
    login,
    get_user_id,
    track,
    register_attributes,
    unregister_attributes,
    clear_attributes,
    track_pageview,
    people: new People()
  },
  {
    get(target: any, prop) {
      if (prop in target) {
        return target[prop]
      } else {
        return () => {
          _apiLog(`方法 ${String(prop)} 暂不支持`)
        }
      }
    },
  }
)

// @ts-ignore
const Countly: any = window.Countly || {}

if (typeof window !== 'undefined') {
  window.Countly = Countly;
  (function () {
    var cly = document.createElement('script')
    cly.type = 'text/javascript';
    cly.async = true
    // 配置sdk的链接，23.6.0.2是SDK的版本号，这个版本号是可用的，如果想获取最新的版本请询问管理员。
    cly.src = 'https://dep176.gsf.netease.com/hubble-to-countly/1.0.9/js-sdk/assets/countly.min.js'
    cly.onload = function () {
      init_from_snippet()
      window.DATracker = DATrackerInner
      window.isCountlyLoaded = true;
      window.readyOnLoadfn?.()
    }
    var s = document.getElementsByTagName('script')[0];
    s.parentNode!.insertBefore(cly, s)
  })()
}